import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, Send, Star, Image } from 'lucide-react';
import CompanyLogo from '../../components/CompanyLogo';

/**
 * ProposalPreviewCard - PDF-style preview of the proposal/estimate
 * 
 * This component renders a preview that matches the printed PDF output.
 * It displays: Company header, Client info, Measurements, Introduction,
 * Reviews, Photos, Scope of Work (itemized or lump sum), Totals, 
 * Terms & Conditions with signature lines, and Footer.
 */
const ProposalPreviewCard = ({
  // Core data
  lead,
  companyInfo,
  settings,
  totals,
  language,
  projectTypeLabels,
  
  // Ref for PDF generation
  estimateRef,
  
  // Actions
  emailPdf,
  generatingPdf,
  
  // Utility functions
  getAllLicenses,
  formatCurrency,
  getPhotoUrl,
  getPhotoFilename
}) => {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gray-800 text-white">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center">
            <FileText className="mr-2 h-5 w-5" />
            {language === 'es' ? 'Vista Previa de Propuesta' : 'Proposal Preview'}
          </CardTitle>
          {lead.client_email && (
            <Button 
              size="sm" 
              onClick={() => emailPdf('estimate')} 
              disabled={generatingPdf}
              className="bg-white text-gray-800 hover:bg-gray-100"
            >
              <Send className="h-4 w-4 mr-1" />
              {language === 'es' ? 'Enviar PDF' : 'Email PDF'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0 max-h-[80vh] overflow-auto bg-gray-100">
        {/* Responsive wrapper - allows horizontal scroll on mobile */}
        <div className="min-w-[620px] md:min-w-0">
          {/* Preview container styled for 8.5x11 letter proportions */}
          <div ref={estimateRef} className="bg-white text-xs mx-auto my-4 shadow-lg" style={{ 
            width: '612px',  /* 8.5 inches at 72 DPI */
            minHeight: '792px', /* 11 inches at 72 DPI */
            padding: '36px', /* 0.5 inch margins */
            boxSizing: 'border-box',
            pageBreakInside: 'auto',
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            hyphens: 'none',
            WebkitHyphens: 'none',
            msHyphens: 'none'
          }}>
            {/* Company Header - keep together */}
            <CompanyHeaderSection companyInfo={companyInfo} getAllLicenses={getAllLicenses} />

            {/* Proposal Title */}
            <ProposalTitleSection lead={lead} companyInfo={companyInfo} language={language} projectTypeLabels={projectTypeLabels} />

            {/* Client Info */}
            <ClientInfoSection lead={lead} companyInfo={companyInfo} language={language} />

            {/* Project Measurements Section - Show if measurement data exists */}
            <ProjectMeasurementsSection lead={lead} companyInfo={companyInfo} language={language} />

            {/* SECTION 1: Introduction Letter */}
            <IntroductionSection lead={lead} companyInfo={companyInfo} language={language} />

            {/* SECTION 1b: Customer Reviews */}
            <ReviewsSection lead={lead} companyInfo={companyInfo} language={language} />

            {/* SECTION 1c: Project Photos */}
            <ProjectPhotosSection 
              lead={lead} 
              companyInfo={companyInfo} 
              language={language}
              getPhotoUrl={getPhotoUrl}
              getPhotoFilename={getPhotoFilename}
            />

            {/* SECTION 2: Line Items or Lump Sum Scope */}
            <ScopeOfWorkSection 
              lead={lead} 
              companyInfo={companyInfo} 
              totals={totals}
              language={language}
              formatCurrency={formatCurrency}
            />

            {/* Totals - Only for Itemized estimates */}
            <TotalsSection 
              lead={lead} 
              companyInfo={companyInfo} 
              totals={totals}
              language={language}
              formatCurrency={formatCurrency}
            />

            {/* SECTION 3: Terms & Conditions */}
            <TermsAndConditionsSection 
              lead={lead} 
              companyInfo={companyInfo} 
              settings={settings}
              language={language}
            />

            {/* Footer */}
            <FooterSection companyInfo={companyInfo} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Company Header Section
 */
const CompanyHeaderSection = ({ companyInfo, getAllLicenses }) => (
  <div 
    style={{ backgroundColor: companyInfo.primaryColor, pageBreakInside: 'avoid', pageBreakAfter: 'avoid' }} 
    className="text-white p-4 -mx-9 -mt-9 mb-4"
  >
    <div className="flex items-center justify-between px-5">
      <div className="flex items-center space-x-2">
        <div className="bg-white p-1.5 rounded">
          {companyInfo.logo ? (
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-8 w-auto" />
          ) : (
            <CompanyLogo className="h-8 w-auto" />
          )}
        </div>
        <div>
          <h1 className="text-base font-bold">{companyInfo.name}</h1>
          <p className="text-amber-100 text-[10px]">
            {getAllLicenses() && `${getAllLicenses()} • `}{companyInfo.tagline}
          </p>
        </div>
      </div>
      <div className="text-right text-[10px]">
        <p>{companyInfo.address}</p>
        <p>{companyInfo.city}, {companyInfo.state} {companyInfo.zip}</p>
        <p>{companyInfo.phone} | {companyInfo.email}</p>
      </div>
    </div>
  </div>
);

/**
 * Proposal Title Section
 */
const ProposalTitleSection = ({ lead, companyInfo, language, projectTypeLabels }) => {
  // Get the project type label for the title
  const getProjectTypeTitle = () => {
    const category = lead.project_category || lead.project_type;
    if (!category) return '';
    
    // Use projectTypeLabels if provided, otherwise format the category
    if (projectTypeLabels && projectTypeLabels[category]) {
      return projectTypeLabels[category].toUpperCase();
    }
    
    // Fallback: format the category string nicely
    return category.replace(/_/g, ' ').toUpperCase();
  };

  const projectType = getProjectTypeTitle();
  
  return (
    <div className="text-center mb-3">
      <h2 className="text-xl font-bold text-gray-800">
        {projectType 
          ? `${projectType} ${language === 'es' ? 'PROPUESTA' : 'PROPOSAL'}`
          : (language === 'es' ? 'PROPUESTA' : 'PROPOSAL')
        }
      </h2>
      {(lead.proposal_id || lead.estimate_number) && (
        <p style={{ color: companyInfo.primaryColor }} className="font-mono text-xs">
          {lead.proposal_id || lead.estimate_number}
        </p>
      )}
      {lead.estimate_date && (
        <p className="text-gray-600 text-[10px]">
          {new Date(lead.estimate_date).toLocaleDateString(
            language === 'es' ? 'es-ES' : 'en-US', 
            { year: 'numeric', month: 'long', day: 'numeric' }
          )}
        </p>
      )}
    </div>
  );
};

/**
 * Client Info Section
 */
const ClientInfoSection = ({ lead, companyInfo, language }) => {
  // Format city, state, zip line
  const cityStateZip = [lead.client_city, lead.client_state, lead.client_zip].filter(Boolean).join(', ');
  
  return (
    <div className="grid grid-cols-2 gap-2 mb-3 text-[10px]">
      <div className="border rounded overflow-hidden" style={{ pageBreakInside: 'avoid' }}>
        <div style={{ backgroundColor: companyInfo.primaryColor }} className="text-white px-2 py-0.5 font-semibold text-[10px]">
          {language === 'es' ? 'Cliente' : 'Client'}
        </div>
        <div className="p-1.5 space-y-0">
          <p><strong>{lead.client_name || '—'}</strong></p>
          <p>{lead.client_address || '—'}</p>
          {cityStateZip && <p>{cityStateZip}</p>}
          <p>{lead.client_phone} | {lead.client_email}</p>
        </div>
      </div>
    </div>
  );
};

/**
 * Project Measurements Section
 */
const ProjectMeasurementsSection = ({ lead, companyInfo, language }) => {
  // Only show if measurement data exists
  if (!(lead.roof_sqft || lead.roof_eaves || lead.roof_rakes || lead.roof_ridge || lead.roof_valleys)) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 gap-2 mb-3 text-[10px]">
      <div className="border rounded overflow-hidden col-span-2" style={{ pageBreakInside: 'avoid' }}>
        <div style={{ backgroundColor: companyInfo.primaryColor }} className="text-white px-2 py-0.5 font-semibold text-[10px]">
          {language === 'es' ? 'Medidas del Proyecto' : 'Project Measurements'}
        </div>
        <div className="p-2">
          <div className="grid grid-cols-5 gap-2 text-center">
            {lead.roof_sqft && (
              <div>
                <p className="font-semibold text-gray-800">{Number(lead.roof_sqft).toLocaleString()}</p>
                <p className="text-[9px] text-gray-500">{language === 'es' ? 'Área (pies²)' : 'Area (sq ft)'}</p>
              </div>
            )}
            {lead.stories && (
              <div>
                <p className="font-semibold text-gray-800">{lead.stories}</p>
                <p className="text-[9px] text-gray-500">{language === 'es' ? 'Pisos' : 'Stories'}</p>
              </div>
            )}
            {lead.roof_pitch && (
              <div>
                <p className="font-semibold text-gray-800 capitalize">
                  {lead.roof_pitch === 'low' ? (language === 'es' ? 'Baja' : 'Low') :
                   lead.roof_pitch === 'standard' ? (language === 'es' ? 'Estándar' : 'Standard') :
                   lead.roof_pitch === 'steep' ? (language === 'es' ? 'Pronunciada' : 'Steep') :
                   lead.roof_pitch}
                </p>
                <p className="text-[9px] text-gray-500">{language === 'es' ? 'Pendiente' : 'Pitch'}</p>
              </div>
            )}
            {lead.roof_eaves && (
              <div>
                <p className="font-semibold text-gray-800">{Number(lead.roof_eaves).toLocaleString()} ft</p>
                <p className="text-[9px] text-gray-500">{language === 'es' ? 'Aleros' : 'Eaves'}</p>
              </div>
            )}
            {lead.roof_rakes && (
              <div>
                <p className="font-semibold text-gray-800">{Number(lead.roof_rakes).toLocaleString()} ft</p>
                <p className="text-[9px] text-gray-500">{language === 'es' ? 'Bordes' : 'Rakes'}</p>
              </div>
            )}
            {lead.roof_ridge && (
              <div>
                <p className="font-semibold text-gray-800">{Number(lead.roof_ridge).toLocaleString()} ft</p>
                <p className="text-[9px] text-gray-500">{language === 'es' ? 'Cumbrera' : 'Ridge'}</p>
              </div>
            )}
            {lead.roof_valleys && (
              <div>
                <p className="font-semibold text-gray-800">{Number(lead.roof_valleys).toLocaleString()} ft</p>
                <p className="text-[9px] text-gray-500">{language === 'es' ? 'Limahoyas' : 'Valleys'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Introduction Section
 */
const IntroductionSection = ({ lead, companyInfo, language }) => {
  if (lead.show_intro_letter === false || !(lead.introduction_letter || companyInfo.introductionLetter)) {
    return null;
  }

  return (
    <div className="mb-4 border rounded-lg overflow-hidden" style={{ pageBreakInside: 'avoid' }}>
      <div style={{ backgroundColor: companyInfo.primaryColor }} className="text-white px-2 py-1 font-semibold text-[10px]">
        {language === 'es' ? 'Carta de Presentación' : 'Introduction'}
      </div>
      <div className="p-3 bg-gray-50">
        <p className="font-semibold text-gray-800 mb-2 text-[11px]">
          Dear {lead.client_name || 'Valued Customer'},
        </p>
        <div className="text-[10px] text-gray-700 whitespace-pre-line leading-snug">
          {lead.introduction_letter || companyInfo.introductionLetter}
        </div>
      </div>
    </div>
  );
};

/**
 * Customer Reviews Section
 */
const ReviewsSection = ({ lead, companyInfo, language }) => {
  if (lead.show_reviews === false || !(lead.reviews_content || companyInfo.reviews)) {
    return null;
  }

  return (
    <div className="mb-4 border rounded-lg overflow-hidden" style={{ pageBreakInside: 'avoid' }}>
      <div style={{ backgroundColor: companyInfo.primaryColor }} className="text-white px-2 py-1 font-semibold text-[10px] flex items-center">
        <Star className="h-3 w-3 mr-1" />
        {language === 'es' ? 'Lo Que Dicen Nuestros Clientes' : 'What Our Clients Say'}
      </div>
      <div className="p-3 bg-amber-50">
        <div className="text-[10px] text-gray-700 whitespace-pre-line leading-snug">
          {lead.reviews_content || companyInfo.reviews}
        </div>
      </div>
    </div>
  );
};

/**
 * Project Photos Section
 */
const ProjectPhotosSection = ({ lead, companyInfo, language, getPhotoUrl, getPhotoFilename }) => {
  if (lead.show_photos_in_estimate === false || !lead.photos?.length) {
    return null;
  }

  return (
    <div className="mb-4 border rounded-lg overflow-hidden" style={{ pageBreakInside: 'avoid' }}>
      <div style={{ backgroundColor: companyInfo.primaryColor }} className="text-white px-2 py-1 font-semibold text-[10px] flex items-center">
        <Image className="h-3 w-3 mr-1" />
        {language === 'es' ? 'Fotos del Proyecto' : 'Project Photos'}
      </div>
      <div className="p-3 bg-gray-50">
        <div className="grid grid-cols-2 gap-2">
          {lead.photos.map((photo, i) => {
            const photoUrl = getPhotoUrl(photo);
            if (!photoUrl) return null;
            return (
              <div key={i} className="text-center">
                <img 
                  src={photoUrl} 
                  alt={`Photo ${i+1}`} 
                  className="w-full h-24 object-cover rounded mb-1"
                />
                <p className="text-[9px] text-gray-600 italic">
                  {getPhotoFilename(photo).replace(/\.[^/.]+$/, '')}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Scope of Work Section (Line Items or Lump Sum)
 */
const ScopeOfWorkSection = ({ lead, companyInfo, totals, language, formatCurrency }) => {
  if (lead.estimate_type === 'lump_sum') {
    return (
      <LumpSumScopeSection 
        lead={lead} 
        companyInfo={companyInfo} 
        totals={totals}
        language={language}
        formatCurrency={formatCurrency}
      />
    );
  }

  // Itemized estimate
  if (!lead.line_items?.length) {
    return null;
  }

  return (
    <div className="mb-3">
      <h3 style={{ backgroundColor: companyInfo.primaryColor }} className="text-white px-2 py-0.5 font-semibold rounded-t text-[10px]">
        {lead.show_itemized_pricing !== false 
          ? (language === 'es' ? 'Partidas del Presupuesto' : 'Estimate Line Items')
          : (language === 'es' ? 'Alcance del Trabajo' : 'Scope of Work')
        }
      </h3>
      {lead.show_itemized_pricing !== false ? (
        /* ITEMIZED VIEW - Show pricing table */
        <table className="w-full border text-[10px]" style={{ pageBreakInside: 'auto' }}>
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-1 border-b">Item</th>
              <th className="text-center p-1 border-b w-10">{language === 'es' ? 'U' : 'Unit'}</th>
              <th className="text-center p-1 border-b w-10">{language === 'es' ? 'Cant' : 'Qty'}</th>
              <th className="text-right p-1 border-b w-14">{language === 'es' ? '$/U' : '$/Unit'}</th>
              <th className="text-right p-1 border-b w-16">Total</th>
            </tr>
          </thead>
          <tbody>
            {lead.line_items.map((item, i) => (
              <tr key={item.id} className={i % 2 === 0 ? 'bg-amber-50' : ''} style={{ pageBreakInside: 'avoid' }}>
                <td className="p-1 border-b" style={{ wordBreak: 'normal' }}>{item.title}</td>
                <td className="p-1 border-b text-center">{item.unit || 'SQ'}</td>
                <td className="p-1 border-b text-center">{item.quantity || 0}</td>
                <td className="p-1 border-b text-right">{formatCurrency(item.unit_price || 0)}</td>
                <td className="p-1 border-b text-right font-semibold">
                  {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        /* NON-ITEMIZED VIEW - Show only scope descriptions */
        <div className="border border-t-0 bg-white">
          {lead.line_items.map((item, i) => (
            <div 
              key={item.id} 
              className={`p-2 border-b last:border-b-0 flex items-start gap-2 ${i % 2 === 0 ? 'bg-amber-50' : ''}`}
            >
              <span className="text-amber-600 font-bold">✓</span>
              <div>
                <span className="text-[10px] font-medium">{item.title}</span>
                {item.description && (
                  <p className="text-[9px] text-gray-500 mt-0.5">{item.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Lump Sum Scope Section
 */
const LumpSumScopeSection = ({ lead, companyInfo, totals, language, formatCurrency }) => (
  <div className="mb-3">
    {(lead.lump_sum_items || []).length > 0 && (
      <>
        <h3 style={{ backgroundColor: companyInfo.primaryColor }} className="text-white px-2 py-0.5 font-semibold rounded-t text-[10px]">
          {language === 'es' ? 'Alcance del Trabajo' : 'Scope of Work'}
        </h3>
        <table className="w-full border text-[10px]" style={{ pageBreakInside: 'auto' }}>
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-1 border-b w-1/3">{language === 'es' ? 'Categoría' : 'Category'}</th>
              <th className="text-left p-1 border-b">{language === 'es' ? 'Descripción' : 'Description'}</th>
            </tr>
          </thead>
          <tbody>
            {(lead.lump_sum_items || []).map((item, i) => (
              <tr key={item.id} className={i % 2 === 0 ? 'bg-blue-50' : ''} style={{ pageBreakInside: 'avoid' }}>
                <td className="p-1 border-b font-medium" style={{ wordBreak: 'normal' }}>{item.category}</td>
                <td className="p-1 border-b" style={{ wordBreak: 'normal' }}>{item.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}
    {/* Lump Sum Total - Show breakdown with overhead/profit if enabled */}
    <div className="flex justify-end mt-3" style={{ pageBreakInside: 'avoid' }}>
      <div className="w-56">
        {/* Show subtotal if overhead or profit is applied */}
        {(lead.apply_overhead || lead.apply_profit) && (
          <div className="flex justify-between py-0.5 border-b text-[10px]">
            <span className="text-gray-600">{language === 'es' ? 'Subtotal' : 'Subtotal'}:</span>
            <span className="font-semibold">{formatCurrency(lead.lump_sum_total || 0)}</span>
          </div>
        )}
        {/* Show Contractor Overhead if enabled */}
        {lead.apply_overhead && (
          <div className="flex justify-between py-0.5 border-b text-[10px]">
            <span className="text-gray-600">{language === 'es' ? 'Gastos Generales' : 'Contractor Overhead'} (10%):</span>
            <span className="font-semibold">{formatCurrency(totals.overheadAmount)}</span>
          </div>
        )}
        {/* Show Contractor Profit if enabled */}
        {lead.apply_profit && (
          <div className="flex justify-between py-0.5 border-b text-[10px]">
            <span className="text-gray-600">{language === 'es' ? 'Ganancia' : 'Contractor Profit'} (10%):</span>
            <span className="font-semibold">{formatCurrency(totals.profitAmount)}</span>
          </div>
        )}
        {/* Grand Total */}
        <div className="flex justify-between py-2 px-3 text-white rounded mt-1 text-[12px]" style={{ backgroundColor: companyInfo.primaryColor }}>
          <span className="font-bold">TOTAL:</span>
          <span className="font-bold text-lg">{formatCurrency(totals.grandTotal)}</span>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Totals Section (for itemized estimates only)
 */
const TotalsSection = ({ lead, companyInfo, totals, language, formatCurrency }) => {
  if (lead.estimate_type === 'lump_sum') {
    return null;
  }

  return (
    <div className="flex justify-end mb-3" style={{ pageBreakInside: 'avoid' }}>
      <div className="w-48">
        <div className="flex justify-between py-0.5 border-b text-[10px]">
          <span className="text-gray-600">{language === 'es' ? 'Subtotal' : 'Subtotal'}:</span>
          <span className="font-semibold">{formatCurrency(totals.subtotal)}</span>
        </div>
        {totals.applyOverhead && (
          <div className="flex justify-between py-0.5 border-b text-[10px]">
            <span className="text-gray-600">{language === 'es' ? 'Gastos Generales' : 'Contractor Overhead'} (10%):</span>
            <span className="font-semibold">{formatCurrency(totals.overheadAmount)}</span>
          </div>
        )}
        {totals.applyProfit && (
          <div className="flex justify-between py-0.5 border-b text-[10px]">
            <span className="text-gray-600">{language === 'es' ? 'Ganancia' : 'Contractor Profit'} (10%):</span>
            <span className="font-semibold">{formatCurrency(totals.profitAmount)}</span>
          </div>
        )}
        <div className="flex justify-between py-1 text-white px-2 rounded mt-1 text-[11px]" style={{ backgroundColor: companyInfo.primaryColor }}>
          <span className="font-bold">TOTAL:</span>
          <span className="font-bold">{formatCurrency(totals.grandTotal)}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Terms and Conditions Section with Signature Lines
 */
const TermsAndConditionsSection = ({ lead, companyInfo, settings, language }) => {
  const termsContent = lead.terms_and_conditions || (language === 'es' ? settings.termsEs : settings.termsEn);
  
  if (!termsContent) {
    return null;
  }

  return (
    <div data-section="terms" className="mb-3 border rounded-lg overflow-hidden" style={{ pageBreakBefore: 'always', pageBreakInside: 'auto' }}>
      <div style={{ backgroundColor: companyInfo.primaryColor }} className="text-white px-2 py-1 font-semibold text-[10px]">
        {language === 'es' ? 'Términos y Condiciones' : 'Terms & Conditions'}
      </div>
      <div className="p-2 bg-gray-50 text-[9px] text-gray-700" style={{ lineHeight: '1.8' }}>
        <div style={{ 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'normal', 
          overflowWrap: 'break-word',
          hyphens: 'none'
        }}>
          {/* Split terms into paragraphs with spacing for better page breaks */}
          {termsContent
            .split(/\n\n|\n(?=[A-Z]{2,})/)
            .map((paragraph, idx) => (
              <p key={idx} style={{ marginBottom: '12px', pageBreakInside: 'avoid' }}>
                {paragraph.trim()}
              </p>
            ))
          }
        </div>
        
        {/* Signature Section */}
        <SignatureSection lead={lead} companyInfo={companyInfo} language={language} />
      </div>
    </div>
  );
};

/**
 * Signature Section
 */
const SignatureSection = ({ lead, companyInfo, language }) => (
  <div className="mt-6 pt-4 border-t border-gray-300" style={{ pageBreakInside: 'avoid' }}>
    <div className="grid grid-cols-2 gap-6">
      {/* Contractor Signature */}
      <div>
        <p className="font-semibold mb-4 text-[10px]">
          {language === 'es' ? 'FIRMA DEL CONTRATISTA' : 'CONTRACTOR SIGNATURE'}
        </p>
        <div className="border-b border-gray-400 mb-1" style={{ height: '24px' }}></div>
        <p className="text-[9px]">{companyInfo.name || 'JY Mega FC Construction Inc.'}</p>
        <div className="mt-3">
          <p className="mb-1 text-[9px]">{language === 'es' ? 'Fecha' : 'Date'}: _______________________</p>
        </div>
      </div>
      
      {/* Client Signature */}
      <div>
        <p className="font-semibold mb-4 text-[10px]">
          {language === 'es' ? 'FIRMA DEL CLIENTE' : 'CLIENT SIGNATURE'}
        </p>
        <div className="border-b border-gray-400 mb-1" style={{ height: '24px' }}></div>
        <p className="text-[9px]">{lead.client_name || '(Client Name)'}</p>
        <div className="mt-3">
          <p className="mb-1 text-[9px]">{language === 'es' ? 'Fecha' : 'Date'}: _______________________</p>
        </div>
      </div>
    </div>
    
    <p className="text-center mt-4 text-gray-500 text-[8px]">
      {language === 'es' 
        ? 'Al firmar arriba, el cliente reconoce haber leído y aceptado todos los términos y condiciones.' 
        : 'By signing above, client acknowledges having read and agreed to all terms and conditions.'}
    </p>
  </div>
);

/**
 * Footer Section
 */
const FooterSection = ({ companyInfo }) => (
  <div data-section="footer" className="mt-3 pt-2 border-t text-center text-[9px] text-gray-500">
    <p>{companyInfo.website} • {companyInfo.email} • {companyInfo.phone}</p>
  </div>
);

export default ProposalPreviewCard;
